﻿import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Text, Heading, Input, SortableTableHeader, LoadingSpinner, Button, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { modulesService } from '../services/modules'
import { downloadXlsx } from '../utils/downloadXlsx'
import {
  superappService,
  behaviorDataToSnapshots,
  normalizeCpfDigits,
  SUPERAPP_LAUNCH_DATE,
  filterDailyMetricSeriesByLastDays,
  milestonesToChartMarkers,
  resolveSuperappUserListStatus,
  getSuperappUserListStatusLabel,
  type SuperappDashboardChartDays
} from '../services/superapp'
import { Tooltip as UiTooltip } from '../components/ui/tooltip'
import { computeLinearRegressionTrend } from '../utils/linearRegressionTrend'
import {
  SuperappUser,
  SuperappUserBehavior,
  DashboardMetrics,
  SuperappUserFilters,
  SuperappPromotion,
  SuperappPromotionFilters,
  SuperappAppIndicationRef,
  SuperappDashboardMilestone,
  BehaviorDataUser,
  SuperappUserListStatus,
  SuperappUserBehaviorStatus,
} from '../types/superapp'
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine,
  LabelList
} from 'recharts'

type ActiveTab = 'dashboard' | 'usuarios' | 'promocoes' | 'momentos_chave'
type DashboardChartTab = 'registrations' | 'lastAccess' | 'assistanceActivations'

type SuperappUserTipo = 'Indicação' | 'Segurado'

const getLast30DaysCreationDates = () => {
  const hoje = new Date()
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  const inicio = new Date(fim)
  inicio.setDate(inicio.getDate() - 30)
  const formatDateInput = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return { inicio: formatDateInput(inicio), fim: formatDateInput(fim) }
}

function getUserIndicationRef(
  cpf: string | null | undefined,
  map: Record<string, SuperappAppIndicationRef>
): SuperappAppIndicationRef | null {
  const digits = normalizeCpfDigits(cpf)
  return digits ? map[digits] ?? null : null
}

function getAssistanceActivationCount(
  cpf: string | null | undefined,
  map: Record<string, number>
): number {
  const digits = normalizeCpfDigits(cpf)
  if (!digits) return 0
  return map[digits] ?? 0
}

function getUserTipo(
  cpf: string | null | undefined,
  map: Record<string, SuperappAppIndicationRef>
): SuperappUserTipo {
  return getUserIndicationRef(cpf, map) ? 'Indicação' : 'Segurado'
}

function formatIndicationBrokerTooltip(ref: SuperappAppIndicationRef): string {
  if (ref.broker_code != null) {
    return `Corretor: ${ref.broker_name} (cód. ${ref.broker_code})`
  }
  return `Corretor: ${ref.broker_name}`
}

type RegistrationChartMarker = {
  id: string
  date: string
  legendLabel: string
  color: string
}

const STATUS_ATIVO_COLOR = '#22c55e'
const STATUS_INATIVO_COLOR = '#ef4444'
const STATUS_SEM_DADOS_COLOR = '#eab308'
const STATUS_CADASTRO_PARCIAL_COLOR = '#3b82f6'

function getSuperappUserStatusColor(status: SuperappUserListStatus): string {
  switch (status) {
    case 'ativo':
      return STATUS_ATIVO_COLOR
    case 'inativo':
      return STATUS_INATIVO_COLOR
    case 'cadastro_parcial':
      return STATUS_CADASTRO_PARCIAL_COLOR
    case 'sem_dados':
      return STATUS_SEM_DADOS_COLOR
  }
}

function renderSuperappUserStatusBadge(status: SuperappUserListStatus) {
  const color = getSuperappUserStatusColor(status)
  return (
    <span className="font-medium" style={{ color }}>
      {getSuperappUserListStatusLabel(status)}
    </span>
  )
}

function formatCountWithRowPercent(count: number, rowTotal: number): string {
  if (rowTotal <= 0) return String(count)
  return `${count} (${((count / rowTotal) * 100).toFixed(1)}%)`
}

function renderRegistrationChartLegend(
  props: { payload?: ReadonlyArray<{ value?: string; color?: string; type?: string }> },
  markers: RegistrationChartMarker[]
) {
  const markerItems = markers.map((m) => ({
    value: m.legendLabel,
    color: m.color,
    type: 'markerLine' as const
  }))
  const items = [...(props.payload ?? []), ...markerItems]

  return (
    <ul
      className="recharts-default-legend"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '4px 16px',
        padding: '8px 0 0',
        margin: 0,
        listStyle: 'none',
        fontSize: 12
      }}
    >
      {items.map((entry, index) => (
        <li
          key={`${entry.value}-${index}`}
          className="recharts-legend-item"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <svg width="16" height="10" aria-hidden>
            {entry.type === 'markerLine' ? (
              <line x1="0" y1="5" x2="16" y2="5" stroke={entry.color} strokeWidth={2} strokeDasharray="4 3" />
            ) : entry.type === 'rect' ? (
              <rect width="12" height="8" fill={entry.color} fillOpacity={0.22} stroke={entry.color} />
            ) : (
              <line x1="0" y1="5" x2="16" y2="5" stroke={entry.color} strokeWidth={2} />
            )}
          </svg>
          <span>{entry.value}</span>
        </li>
      ))}
    </ul>
  )
}

function UserBehaviorSnapshotPanel({
  snapshot: user,
  formatDate,
  formatCpf,
  formatPhone,
  getOpenStreetMapEmbedUrl,
}: {
  snapshot: BehaviorDataUser
  formatDate: (dateString: string | null | undefined) => string
  formatCpf: (cpf: string | null | undefined) => string
  formatPhone: (phone: string | null | undefined) => string
  getOpenStreetMapEmbedUrl: (latitude: number, longitude: number) => string
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          CPF (snapshot)
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {formatCpf(user.cpf)}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Nome (snapshot)
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.name || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Email (snapshot)
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.email || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Telefone (snapshot)
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {formatPhone(user.phone)}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Última visualização
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.atividade?.last_seen_at ? formatDate(user.atividade.last_seen_at) : '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Primeira visualização
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.atividade?.first_seen_at ? formatDate(user.atividade.first_seen_at) : '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Dias de vida do cliente
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.atividade?.customer_lifetime_days ?? '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Dias desde última atividade
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.atividade?.days_since_last_activity ?? '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Sistema operacional
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.dispositivo?.device_os || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Versão do app
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.dispositivo?.app_version || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Cidade
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.localizacao?.city || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Status ativo
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.is_active ? 'Sim' : 'Não'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Risco de churn
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.risk_churn ? 'Sim' : 'Não'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Última ação
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.last_action || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Total de eventos
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.total_events ?? '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Módulo favorito
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.favorite_module || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Score de engajamento
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.engagement_score ?? '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Horário preferido
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.comportamento?.preferred_time_slot || '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Latitude
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.localizacao?.latitude ?? '-'}
        </Text>
      </div>
      <div>
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Longitude
        </Text>
        <Text size="sm" className="text-gray-900 dark:text-white">
          {user.localizacao?.longitude ?? '-'}
        </Text>
      </div>
      <div className="sm:col-span-2">
        <Text size="md" className="text-gray-900 dark:text-gray-100 font-semibold mb-2">
          Mapa da localização
        </Text>
        {typeof user.localizacao?.latitude === 'number' &&
        typeof user.localizacao?.longitude === 'number' ? (
          <div className="rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
            <iframe
              title={`Mapa - ${user.name || user.cpf || 'usuário'}`}
              src={getOpenStreetMapEmbedUrl(user.localizacao.latitude, user.localizacao.longitude)}
              className="w-full h-64"
              loading="lazy"
            />
          </div>
        ) : (
          <Text size="sm" className="text-gray-500 dark:text-gray-400">
            Localização indisponível para exibir mapa.
          </Text>
        )}
      </div>
    </div>
  )
}

const SuperappDashboard: React.FC = () => {
  // Estados gerais
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard')
  const [moduleTitle, setModuleTitle] = useState<string>('Superapp Dashboard')
  const [moduleDescription, setModuleDescription] = useState<string>('Dashboard de análise de comportamento e gestão de usuários')

  // Estados Dashboard
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics | null>(null)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [registrationsChartDays, setRegistrationsChartDays] = useState<SuperappDashboardChartDays>(60)
  const [lastAccessChartDays, setLastAccessChartDays] = useState<SuperappDashboardChartDays>(60)
  const [assistanceActivationsChartDays, setAssistanceActivationsChartDays] =
    useState<SuperappDashboardChartDays>(60)
  const [dashboardChartTab, setDashboardChartTab] = useState<DashboardChartTab>('registrations')
  const [dashboardMilestones, setDashboardMilestones] = useState<SuperappDashboardMilestone[]>([])

  // Modal de comportamentos (aberto ao clicar em usuário)
  const [selectedBehavior, setSelectedBehavior] = useState<SuperappUserBehavior | null>(null)

  // Estados Usuários
  const [usersData, setUsersData] = useState<SuperappUser[]>([])
  const [filtroNomeUsuario, setFiltroNomeUsuario] = useState('')
  const [filtroEmailUsuario, setFiltroEmailUsuario] = useState('')
  const [filtroCpfUsuario, setFiltroCpfUsuario] = useState('')
  const [filtroTelefoneUsuario, setFiltroTelefoneUsuario] = useState('')
  const [filtroEmpresaUsuario, setFiltroEmpresaUsuario] = useState('')
  const defaultUserCreationDates = getLast30DaysCreationDates()
  const [filtroCriacaoInicio, setFiltroCriacaoInicio] = useState(defaultUserCreationDates.inicio)
  const [filtroCriacaoFim, setFiltroCriacaoFim] = useState(defaultUserCreationDates.fim)
  const [filtroRiscoChurn, setFiltroRiscoChurn] = useState<'' | 'sim' | 'nao'>('')
  const [filtroStatus, setFiltroStatus] = useState<
    '' | 'ativo' | 'inativo' | 'sem_dados' | 'cadastro_parcial'
  >('')
  const [filtroTipo, setFiltroTipo] = useState<'' | 'segurado' | 'indicacao'>('')
  const [usersBehaviorStatusMap, setUsersBehaviorStatusMap] = useState<
    Record<string, SuperappUserBehaviorStatus>
  >({})
  const [appIndicationByCpf, setAppIndicationByCpf] = useState<Record<string, SuperappAppIndicationRef>>({})
  const [assistanceActivationByCpf, setAssistanceActivationByCpf] = useState<Record<string, number>>({})
  const [filtroAcionamentoAssistencias, setFiltroAcionamentoAssistencias] = useState<'' | 'com' | 'sem'>('')
  const [selectedUser, setSelectedUser] = useState<SuperappUser | null>(null)
  const [selectedUserBehaviorCount, setSelectedUserBehaviorCount] = useState<number>(0)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [userModalLoading, setUserModalLoading] = useState(false)
  const [sortByUser, setSortByUser] = useState<'user_id' | 'name' | 'company' | 'email' | 'cpf' | 'phone' | 'tipo' | 'status' | 'acionamento_assistencias' | 'created_at'>('created_at')
  const [sortOrderUser, setSortOrderUser] = useState<'asc' | 'desc'>('desc')
  const [currentPageUser, setCurrentPageUser] = useState(1)
  const { hasPermission: canViewValues } = usePermission('superapp_dashboard', 'view_values')
  const { hasPermission: canDownloadInfo } = usePermission('superapp_dashboard', 'download_info')
  const { hasPermission: canInsertMilestone, loading: insertMilestonePermLoading } = usePermission('superapp_dashboard', 'insert')
  const { hasPermission: canUpdateMilestone, loading: updateMilestonePermLoading } = usePermission('superapp_dashboard', 'update')
  const { hasPermission: canDeleteMilestone, loading: deleteMilestonePermLoading } = usePermission('superapp_dashboard', 'delete')
  const milestonesPermissionsLoading =
    insertMilestonePermLoading || updateMilestonePermLoading || deleteMilestonePermLoading
  const canAccessMomentosChaveTab =
    canInsertMilestone || canUpdateMilestone || canDeleteMilestone

  // Estados Promoções
  const [promotionsData, setPromotionsData] = useState<SuperappPromotion[]>([])
  const [filtroTitle, setFiltroTitle] = useState('')
  const [filtroEstablishmentName, setFiltroEstablishmentName] = useState('')
  const [filtroStartDate, setFiltroStartDate] = useState('')
  const [filtroEndDate, setFiltroEndDate] = useState('')
  const [filtroType, setFiltroType] = useState('')
  const [selectedPromotion, setSelectedPromotion] = useState<SuperappPromotion | null>(null)
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false)
  const [sortByPromotion, setSortByPromotion] = useState<'title' | 'establishment_name' | 'start_date' | 'end_date' | 'type'>('start_date')
  const [sortOrderPromotion, setSortOrderPromotion] = useState<'asc' | 'desc'>('desc')
  const [currentPagePromotion, setCurrentPagePromotion] = useState(1)

  // Estados Momentos-Chave
  const [milestonesLoading, setMilestonesLoading] = useState(false)
  const [milestoneFormName, setMilestoneFormName] = useState('')
  const [milestoneFormDate, setMilestoneFormDate] = useState('')
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null)
  const [milestoneSaving, setMilestoneSaving] = useState(false)
  const [milestoneFormError, setMilestoneFormError] = useState<string | null>(null)
  const [sortOrderMilestone, setSortOrderMilestone] = useState<'asc' | 'desc'>('asc')

  // Carregar informações do módulo
  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName('superapp_dashboard')
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    loadModuleInfo()
  }, [])

  // Carregar métricas do dashboard
  useEffect(() => {
    const loadDashboardMetrics = async () => {
      if (activeTab !== 'dashboard') return

      try {
        setDashboardLoading(true)
        const [metrics, milestones] = await Promise.all([
          superappService.getDashboardMetrics(),
          superappService.getDashboardMilestones(),
        ])
        setDashboardMetrics(metrics)
        setDashboardMilestones(milestones)
      } catch (err: any) {
        console.error('Erro ao carregar métricas:', err)
        setError(err.message)
      } finally {
        setDashboardLoading(false)
      }
    }

    loadDashboardMetrics()
  }, [activeTab])

  // Carregar usuários e mapa de status (comportamento) para filtros
  useEffect(() => {
    const loadUsers = async () => {
      if (activeTab !== 'usuarios') return

      try {
        setLoading(true)
        const filters: SuperappUserFilters = {
          name: filtroNomeUsuario || undefined,
          email: filtroEmailUsuario || undefined,
          cpf: filtroCpfUsuario || undefined,
          phone: filtroTelefoneUsuario || undefined,
          company: filtroEmpresaUsuario || undefined,
          created_from: filtroCriacaoInicio || undefined,
          created_to: filtroCriacaoFim || undefined,
        }
        const [users, statusMap, indicationMap, activationMap] = await Promise.all([
          superappService.getAllUsers(filters),
          superappService.getUsersBehaviorStatusMap(),
          superappService.getAppIndicationByCpfMap(),
          superappService.getAssistanceActivationCountByCpfMap(),
        ])
        setUsersData(users)
        setUsersBehaviorStatusMap(statusMap)
        setAppIndicationByCpf(indicationMap)
        setAssistanceActivationByCpf(activationMap)
      } catch (err: any) {
        console.error('Erro ao carregar usuários:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadUsers()
  }, [activeTab, filtroNomeUsuario, filtroEmailUsuario, filtroCpfUsuario, filtroTelefoneUsuario, filtroEmpresaUsuario, filtroCriacaoInicio, filtroCriacaoFim])

  // Carregar promoções
  useEffect(() => {
    const loadPromotions = async () => {
      if (activeTab !== 'promocoes') return

      try {
        setLoading(true)
        const filters: SuperappPromotionFilters = {
          title: filtroTitle || undefined,
          establishment_name: filtroEstablishmentName || undefined,
          start_date: filtroStartDate || undefined,
          end_date: filtroEndDate || undefined,
          type: filtroType || undefined
        }
        const promotions = await superappService.getPromotions(filters)
        setPromotionsData(promotions)
      } catch (err: any) {
        console.error('Erro ao carregar promoções:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadPromotions()
  }, [activeTab, filtroTitle, filtroEstablishmentName, filtroStartDate, filtroEndDate, filtroType])

  // Carregar marcos (aba Momentos-Chave)
  useEffect(() => {
    const loadMilestones = async () => {
      if (activeTab !== 'momentos_chave') return

      try {
        setMilestonesLoading(true)
        const milestones = await superappService.getDashboardMilestones()
        setDashboardMilestones(milestones)
      } catch (err: any) {
        console.error('Erro ao carregar marcos:', err)
        setError(err.message)
      } finally {
        setMilestonesLoading(false)
      }
    }

    loadMilestones()
  }, [activeTab])

  // Redireciona se a aba Momentos-Chave for acessada sem permissão de gestão
  useEffect(() => {
    if (milestonesPermissionsLoading) return
    if (activeTab === 'momentos_chave' && !canAccessMomentosChaveTab) {
      setActiveTab('dashboard')
    }
  }, [activeTab, milestonesPermissionsLoading, canAccessMomentosChaveTab])

  // Filtrar e ordenar usuários
  const usersFiltrados = useMemo(() => {
    let filtered = [...usersData]

    // Filtro por status, risco de churn, tipo e acionamento de assistências
    if (filtroStatus || filtroRiscoChurn || filtroTipo || filtroAcionamentoAssistencias) {
      filtered = filtered.filter((user) => {
        const behavior = usersBehaviorStatusMap[user.user_id]
        const status = resolveSuperappUserListStatus(user, behavior)
        if (filtroStatus && status !== filtroStatus) return false
        if (filtroRiscoChurn === 'sim' && !behavior?.risk_churn) return false
        if (filtroRiscoChurn === 'nao' && behavior?.risk_churn) return false
        const tipo = getUserTipo(user.cpf, appIndicationByCpf)
        if (filtroTipo === 'indicacao' && tipo !== 'Indicação') return false
        if (filtroTipo === 'segurado' && tipo !== 'Segurado') return false
        const activationCount = getAssistanceActivationCount(user.cpf, assistanceActivationByCpf)
        if (filtroAcionamentoAssistencias === 'com' && activationCount <= 0) return false
        if (filtroAcionamentoAssistencias === 'sem' && activationCount !== 0) return false
        return true
      })
    }

    // Aplicar ordenação
    filtered.sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortByUser === 'status') {
        const rank = (u: SuperappUser) => {
          const status = resolveSuperappUserListStatus(u, usersBehaviorStatusMap[u.user_id])
          switch (status) {
            case 'sem_dados':
              return 0
            case 'cadastro_parcial':
              return 1
            case 'inativo':
              return 2
            case 'ativo':
              return 3
          }
        }
        aValue = rank(a)
        bValue = rank(b)
      } else if (sortByUser === 'tipo') {
        const rank = (u: SuperappUser) => (getUserTipo(u.cpf, appIndicationByCpf) === 'Indicação' ? 1 : 0)
        aValue = rank(a)
        bValue = rank(b)
      } else if (sortByUser === 'acionamento_assistencias') {
        aValue = getAssistanceActivationCount(a.cpf, assistanceActivationByCpf)
        bValue = getAssistanceActivationCount(b.cpf, assistanceActivationByCpf)
      } else {
        aValue = a[sortByUser]
        bValue = b[sortByUser]
      }

      if (sortByUser === 'created_at') {
        // Tratar valores nulos: null/undefined vão para o final
        if (!aValue && !bValue) return 0
        if (!aValue) return 1
        if (!bValue) return -1
        const aDate = new Date(aValue).getTime()
        const bDate = new Date(bValue).getTime()
        aValue = isNaN(aDate) ? 0 : aDate
        bValue = isNaN(bDate) ? 0 : bDate
      } else if (
        sortByUser !== 'status' &&
        sortByUser !== 'tipo' &&
        sortByUser !== 'acionamento_assistencias'
      ) {
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }

      if (sortOrderUser === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    return filtered
  }, [
    usersData,
    sortByUser,
    sortOrderUser,
    filtroStatus,
    filtroRiscoChurn,
    filtroTipo,
    filtroAcionamentoAssistencias,
    usersBehaviorStatusMap,
    appIndicationByCpf,
    assistanceActivationByCpf,
  ])

  // Filtrar e ordenar promoções
  const promotionsFiltradas = useMemo(() => {
    let filtered = [...promotionsData]

    // Aplicar ordenação
    filtered.sort((a, b) => {
      let aValue: any = a[sortByPromotion]
      let bValue: any = b[sortByPromotion]

      if (sortByPromotion === 'start_date' || sortByPromotion === 'end_date') {
        // Tratar valores nulos: null/undefined vão para o final
        if (!aValue && !bValue) return 0
        if (!aValue) return 1
        if (!bValue) return -1
        const aDate = new Date(aValue).getTime()
        const bDate = new Date(bValue).getTime()
        aValue = isNaN(aDate) ? 0 : aDate
        bValue = isNaN(bDate) ? 0 : bDate
      } else {
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }

      if (sortOrderPromotion === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    return filtered
  }, [promotionsData, sortByPromotion, sortOrderPromotion])

  // Paginação
  const itemsPerPage = 20
  const totalPagesUser = Math.ceil(usersFiltrados.length / itemsPerPage)
  const startIndexUser = (currentPageUser - 1) * itemsPerPage
  const endIndexUser = startIndexUser + itemsPerPage
  const usersPaginados = usersFiltrados.slice(startIndexUser, endIndexUser)

  const totalPagesPromotion = Math.ceil(promotionsFiltradas.length / itemsPerPage)
  const startIndexPromotion = (currentPagePromotion - 1) * itemsPerPage
  const endIndexPromotion = startIndexPromotion + itemsPerPage
  const promotionsPaginadas = promotionsFiltradas.slice(startIndexPromotion, endIndexPromotion)

  // Handlers de paginação
  const handlePageChangeUser = (page: number) => {
    setCurrentPageUser(page)
  }

  const handlePreviousPageUser = () => {
    if (currentPageUser > 1) {
      setCurrentPageUser(currentPageUser - 1)
    }
  }

  const handleNextPageUser = () => {
    if (currentPageUser < totalPagesUser) {
      setCurrentPageUser(currentPageUser + 1)
    }
  }

  const handlePageChangePromotion = (page: number) => {
    setCurrentPagePromotion(page)
  }

  const handlePreviousPagePromotion = () => {
    if (currentPagePromotion > 1) {
      setCurrentPagePromotion(currentPagePromotion - 1)
    }
  }

  const handleNextPagePromotion = () => {
    if (currentPagePromotion < totalPagesPromotion) {
      setCurrentPagePromotion(currentPagePromotion + 1)
    }
  }

  // Navegação horizontal (tabelas)
  const usersScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollUsersBy = (dx: number) => {
    usersScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handleUsersWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      usersScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  const promotionsScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollPromotionsBy = (dx: number) => {
    promotionsScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handlePromotionsWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      promotionsScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPageUser(1)
  }, [filtroNomeUsuario, filtroEmailUsuario, filtroCpfUsuario, filtroTelefoneUsuario, filtroEmpresaUsuario, filtroCriacaoInicio, filtroCriacaoFim, filtroStatus, filtroRiscoChurn, filtroTipo, filtroAcionamentoAssistencias])

  useEffect(() => {
    setCurrentPagePromotion(1)
  }, [filtroTitle, filtroEstablishmentName, filtroStartDate, filtroEndDate, filtroType])

  // Handlers
  const toggleSortUser = (key: string) => {
    if (sortByUser === key) {
      setSortOrderUser(sortOrderUser === 'asc' ? 'desc' : 'asc')
    } else {
      setSortByUser(key as any)
      setSortOrderUser('asc')
    }
  }

  const toggleSortPromotion = (key: string) => {
    if (sortByPromotion === key) {
      setSortOrderPromotion(sortOrderPromotion === 'asc' ? 'desc' : 'asc')
    } else {
      setSortByPromotion(key as any)
      setSortOrderPromotion('asc')
    }
  }

  const toggleSortMilestone = () => {
    setSortOrderMilestone((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const resetMilestoneForm = () => {
    setMilestoneFormName('')
    setMilestoneFormDate('')
    setEditingMilestoneId(null)
    setMilestoneFormError(null)
  }

  const refreshMilestones = async () => {
    const milestones = await superappService.getDashboardMilestones()
    setDashboardMilestones(milestones)
    return milestones
  }

  const handleSaveMilestone = async () => {
    const name = milestoneFormName.trim()
    if (!name || !milestoneFormDate) {
      setMilestoneFormError('Informe o nome e a data do marco.')
      return
    }

    if (editingMilestoneId) {
      if (!canUpdateMilestone) {
        setMilestoneFormError('Você não tem permissão para editar marcos.')
        return
      }
    } else if (!canInsertMilestone) {
      setMilestoneFormError('Você não tem permissão para criar marcos.')
      return
    }

    try {
      setMilestoneSaving(true)
      setMilestoneFormError(null)
      const payload = { name, event_date: milestoneFormDate }
      if (editingMilestoneId) {
        await superappService.updateDashboardMilestone(editingMilestoneId, payload)
      } else {
        await superappService.createDashboardMilestone(payload)
      }
      await refreshMilestones()
      resetMilestoneForm()
    } catch (err: any) {
      console.error('Erro ao salvar marco:', err)
      setMilestoneFormError(err.message || 'Erro ao salvar marco.')
    } finally {
      setMilestoneSaving(false)
    }
  }

  const handleEditMilestone = (milestone: SuperappDashboardMilestone) => {
    if (!canUpdateMilestone) return
    setEditingMilestoneId(milestone.id)
    setMilestoneFormName(milestone.name)
    setMilestoneFormDate(milestone.event_date)
    setMilestoneFormError(null)
  }

  const handleDeleteMilestone = async (id: string) => {
    if (!canDeleteMilestone) return
    if (!window.confirm('Excluir este marco? Ele deixará de aparecer nos gráficos.')) return

    try {
      setMilestoneSaving(true)
      await superappService.deleteDashboardMilestone(id)
      if (editingMilestoneId === id) {
        resetMilestoneForm()
      }
      await refreshMilestones()
    } catch (err: any) {
      console.error('Erro ao excluir marco:', err)
      setMilestoneFormError(err.message || 'Erro ao excluir marco.')
    } finally {
      setMilestoneSaving(false)
    }
  }

  const handleUserRowClick = async (item: SuperappUser) => {
    setSelectedUser(item)
    setSelectedBehavior(null)
    setSelectedUserBehaviorCount(0)
    setUserModalLoading(true)
    setIsUserModalOpen(true)

    try {
      const [behavior, count] = await Promise.all([
        superappService.getLatestUserBehaviorByUserId(item.user_id),
        superappService.getUserBehaviorCount(item.user_id),
      ])
      setSelectedBehavior(behavior)
      setSelectedUserBehaviorCount(count)
    } catch (err) {
      console.error('Erro ao carregar dados do usuário:', err)
    } finally {
      setUserModalLoading(false)
    }
  }

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false)
    setSelectedUser(null)
    setSelectedBehavior(null)
    setSelectedUserBehaviorCount(0)
    setUserModalLoading(false)
  }

  const handlePromotionRowClick = (item: SuperappPromotion) => {
    setSelectedPromotion(item)
    setIsPromotionModalOpen(true)
  }

  const handleClosePromotionModal = () => {
    setIsPromotionModalOpen(false)
    setSelectedPromotion(null)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    // Verificar se a data é válida
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Sao_Paulo'
    })
  }

  const formatDateShort = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    
    // Se a string está no formato YYYY-MM-DD, interpretar como data local em São Paulo
    // para evitar problemas de timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      // Criar data no timezone de São Paulo
      const dateSP = new Date(year, month - 1, day)
      return dateSP.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
    
    // Para outros formatos, usar o método padrão
    const date = new Date(dateString)
    // Verificar se a data é válida
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo'
    })
  }

  const formatWeekdayName = (dateString: string | null | undefined) => {
    if (!dateString) return ''

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      const dateSP = new Date(year, month - 1, day)
      return dateSP.toLocaleDateString('pt-BR', { weekday: 'long' })
    }

    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      timeZone: 'America/Sao_Paulo'
    })
  }

  const formatCpf = (cpf: string | null | undefined): string => {
    if (!cpf) return '-'
    // Remove caracteres não numéricos
    const cleaned = cpf.replace(/\D/g, '')
    // Aplica a máscara XXX.XXX.XXX-XX
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
    // Se não tiver 11 dígitos, retorna sem formatação
    return cpf
  }

  const formatPhone = (phone: string | null | undefined): string => {
    if (!phone) return '-'

    let cleaned = phone.replace(/\D/g, '')

    // Remove prefixo de país (55) quando presente.
    if ((cleaned.length === 12 || cleaned.length === 13) && cleaned.startsWith('55')) {
      cleaned = cleaned.slice(2)
    }

    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    }
    if (cleaned.length === 9) {
      return cleaned.replace(/(\d{5})(\d{4})/, '$1-$2')
    }
    if (cleaned.length === 8) {
      return cleaned.replace(/(\d{4})(\d{4})/, '$1-$2')
    }

    return phone
  }

  const exportUsersExcel = () => {
    if (!canDownloadInfo || usersFiltrados.length === 0) return
    const rows = usersFiltrados.map((user) => {
      const indicationRef = getUserIndicationRef(user.cpf, appIndicationByCpf)
      const tipo = indicationRef ? 'Indicação' : 'Segurado'
      const behavior = usersBehaviorStatusMap[user.user_id]
      const status = getSuperappUserListStatusLabel(resolveSuperappUserListStatus(user, behavior))
      const riscoChurn =
        behavior === undefined ? '-' : behavior.risk_churn ? 'Sim' : 'Não'
      const corretor =
        indicationRef != null
          ? indicationRef.broker_code != null
            ? `${indicationRef.broker_name} (cód. ${indicationRef.broker_code})`
            : indicationRef.broker_name
          : ''
      return {
        Nome: user.name || '',
        CPF: formatCpf(user.cpf),
        Email: user.email || '',
        Telefone: formatPhone(user.phone),
        Empresa: user.company || '',
        Apólice: user.policy_number != null ? String(user.policy_number) : '',
        Subgrupo: user.subgroup != null ? String(user.subgroup) : '',
        Tipo: tipo,
        Corretor: corretor,
        Status: status,
        'Risco de churn': riscoChurn,
        'Acionamento assistências': getAssistanceActivationCount(user.cpf, assistanceActivationByCpf),
        'Data de Criação': formatDate(user.created_at),
      }
    })
    const dateStamp = new Date().toISOString().slice(0, 10)
    downloadXlsx('Usuários', `usuarios-superapp-${dateStamp}.xlsx`, rows)
  }

  const formatJson = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      return JSON.stringify(data, null, 2)
    } catch (error) {
      return String(data)
    }
  }

  const getOpenStreetMapEmbedUrl = (latitude: number, longitude: number): string => {
    // Pequena margem para manter o ponto visível no iframe.
    const delta = 0.01
    const bbox = [
      longitude - delta,
      latitude - delta,
      longitude + delta,
      latitude + delta
    ].join('%2C')
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude}%2C${longitude}`
  }

  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '-'
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const isPromotionActive = (startDate: string, endDate: string): boolean => {
    const now = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)
    return now >= start && now <= end
  }

  const getUserNameFromBehavior = (behavior: SuperappUserBehavior): string => {
    // Prioriza o nome do relacionamento com superapp_users
    if (behavior.user?.name) {
      return behavior.user.name
    }
    // Se não tiver no relacionamento, busca no JSON behavior_data (objeto ou array legado)
    const snap = behaviorDataToSnapshots(behavior.behavior_data)[0]
    return snap?.name || '-'
  }

  const getUserEmailFromBehavior = (behavior: SuperappUserBehavior): string => {
    // Prioriza o email do relacionamento com superapp_users
    if (behavior.user?.email) {
      return behavior.user.email
    }
    const snap = behaviorDataToSnapshots(behavior.behavior_data)[0]
    return snap?.email || '-'
  }

  const getUserPhoneFromBehavior = (behavior: SuperappUserBehavior): string => {
    // Prioriza o telefone do relacionamento com superapp_users
    if (behavior.user?.phone) {
      return formatPhone(behavior.user.phone)
    }
    const snap = behaviorDataToSnapshots(behavior.behavior_data)[0]
    return formatPhone(snap?.phone)
  }

  const statusDistributionSummary = useMemo(() => {
    if (!dashboardMetrics) {
      return { ativo: 0, inativo: 0, semDados: 0, cadastroParcial: 0 }
    }
    const by = new Map(dashboardMetrics.usersByStatus.map((s) => [s.status, s.count]))
    return {
      ativo: by.get('Ativo') ?? 0,
      inativo: by.get('Inativo') ?? 0,
      semDados: by.get('Sem dados') ?? 0,
      cadastroParcial: by.get('Cadastro parcial') ?? 0
    }
  }, [dashboardMetrics])

  const postLaunchDesistenciaPercents = useMemo(() => {
    if (!dashboardMetrics) {
      return { sobreNovos: null as number | null }
    }
    const n = dashboardMetrics.newUsersSinceLaunch20260513
    const s =
      dashboardMetrics.usersSemDadosCadastroSinceLaunch20260513 +
      dashboardMetrics.usersCadastroParcialSinceLaunch20260513
    return {
      sobreNovos: n > 0 ? (s / n) * 100 : null
    }
  }, [dashboardMetrics])

  const usersWithoutActivationSinceLaunch = useMemo(() => {
    if (!dashboardMetrics) return 0
    return (
      dashboardMetrics.usersSemDadosCadastroSinceLaunch20260513 +
      dashboardMetrics.usersCadastroParcialSinceLaunch20260513
    )
  }, [dashboardMetrics])

  const pieChartData = useMemo(() => {
    if (!dashboardMetrics) return []
    return dashboardMetrics.usersByStatus
      .map((s) => ({
        name: s.status,
        value: s.count
      }))
      .filter((s) => s.value > 0)
  }, [dashboardMetrics])

  const chartMarkers = useMemo(
    () => milestonesToChartMarkers(dashboardMilestones),
    [dashboardMilestones]
  )

  const usersByCityChartHeight = useMemo(
    () => Math.max(280, (dashboardMetrics?.usersByCity.length ?? 0) * 36),
    [dashboardMetrics?.usersByCity.length]
  )

  const sortedMilestones = useMemo(() => {
    const sorted = [...dashboardMilestones]
    sorted.sort((a, b) => {
      const cmp = a.event_date.localeCompare(b.event_date) || a.name.localeCompare(b.name)
      return sortOrderMilestone === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [dashboardMilestones, sortOrderMilestone])

  const { registrationsByDateChartData, registrationChartMarkers, showRegistrationLines } = useMemo(() => {
    const source = filterDailyMetricSeriesByLastDays(
      dashboardMetrics?.behaviorsByDate ?? [],
      registrationsChartDays
    )
    const markerConfigs = chartMarkers
    if (!source.length) {
      return {
        registrationsByDateChartData: [],
        registrationChartMarkers: [] as RegistrationChartMarker[],
        showRegistrationLines: { ativo: false, inativo: false, semDados: false, cadastroParcial: false }
      }
    }
    const minDate = source[0].date
    const maxDate = source[source.length - 1].date
    let baseRows = [...source]
    const visibleMarkers = markerConfigs.filter(({ date }) => {
      const inRange = date >= minDate && date <= maxDate
      if (!inRange) return false
      if (!baseRows.some((r) => r.date === date)) {
        baseRows.push({ date, ativo: 0, inativo: 0, semDados: 0, cadastroParcial: 0, count: 0 })
      }
      return true
    })
    baseRows.sort((a, b) => a.date.localeCompare(b.date))
    const rows = baseRows.map((r) => ({
      ...r,
      ativoLine: r.ativo > 0 ? r.ativo : null,
      inativoLine: r.inativo > 0 ? r.inativo : null,
      semDadosLine: r.semDados > 0 ? r.semDados : null,
      cadastroParcialLine: r.cadastroParcial > 0 ? r.cadastroParcial : null
    }))
    return {
      registrationsByDateChartData: rows,
      registrationChartMarkers: visibleMarkers,
      showRegistrationLines: {
        ativo: rows.some((r) => r.ativo > 0),
        inativo: rows.some((r) => r.inativo > 0),
        semDados: rows.some((r) => r.semDados > 0),
        cadastroParcial: rows.some((r) => r.cadastroParcial > 0)
      }
    }
  }, [dashboardMetrics?.behaviorsByDate, registrationsChartDays, chartMarkers])

  const {
    lastAccessByDateChartData,
    lastAccessChartMarkers,
    hasLastAccessTrendLine,
    hasLastAccessAvgLine
  } = useMemo(() => {
    const source = filterDailyMetricSeriesByLastDays(
      dashboardMetrics?.lastAccessByDate ?? [],
      lastAccessChartDays
    )
    if (!source.length) {
      return {
        lastAccessByDateChartData: [],
        lastAccessChartMarkers: [] as RegistrationChartMarker[],
        hasLastAccessTrendLine: false,
        hasLastAccessAvgLine: false
      }
    }
    const minDate = source[0].date
    const maxDate = source[source.length - 1].date
    const visibleMarkers = chartMarkers.filter(
      ({ date }) => date >= minDate && date <= maxDate
    )
    const inRange = SUPERAPP_LAUNCH_DATE >= minDate && SUPERAPP_LAUNCH_DATE <= maxDate
    let baseRows = source
    if (inRange && !source.some((r) => r.date === SUPERAPP_LAUNCH_DATE)) {
      baseRows = [
        ...source,
        { date: SUPERAPP_LAUNCH_DATE, ativo: 0, inativo: 0, semDados: 0, cadastroParcial: 0, count: 0 }
      ].sort((a, b) => a.date.localeCompare(b.date))
    }
    const trendStartIdx = baseRows.findIndex((r) => r.date >= SUPERAPP_LAUNCH_DATE)
    const countsForTrend = baseRows.map((r, i) =>
      trendStartIdx >= 0 && i >= trendStartIdx && r.count > 0 ? r.count : null
    )
    const trendValues = computeLinearRegressionTrend(countsForTrend)
    const hasTrendLine =
      trendStartIdx >= 0 && trendValues.some((v, i) => i >= trendStartIdx && v != null)

    const postLaunchRows = trendStartIdx >= 0 ? baseRows.slice(trendStartIdx) : []
    const postLaunchSum = postLaunchRows.reduce((acc, r) => acc + r.count, 0)
    const avgSinceLaunch =
      postLaunchRows.length > 0
        ? Math.round((postLaunchSum / postLaunchRows.length) * 10) / 10
        : null
    const hasAvgLine = avgSinceLaunch != null && avgSinceLaunch > 0

    return {
      lastAccessByDateChartData: baseRows.map((r, i) => ({
        ...r,
        trendLine:
          trendStartIdx >= 0 && i >= trendStartIdx && hasTrendLine
            ? trendValues[i] ?? null
            : null,
        avgLine:
          trendStartIdx >= 0 && i >= trendStartIdx && hasAvgLine ? avgSinceLaunch : null
      })),
      lastAccessChartMarkers: visibleMarkers,
      hasLastAccessTrendLine: hasTrendLine,
      hasLastAccessAvgLine: hasAvgLine
    }
  }, [dashboardMetrics?.lastAccessByDate, lastAccessChartDays, chartMarkers])

  const {
    assistanceActivationsByDateChartData,
    assistanceActivationsChartMarkers
  } = useMemo(() => {
    const source = filterDailyMetricSeriesByLastDays(
      (dashboardMetrics?.assistanceActivationsByDate ?? []).map((r) => ({
        date: r.date,
        count: r.count,
        ativo: 0,
        inativo: 0,
        semDados: 0,
        cadastroParcial: 0
      })),
      assistanceActivationsChartDays
    )
    if (!source.length) {
      return {
        assistanceActivationsByDateChartData: [] as Array<{ date: string; count: number }>,
        assistanceActivationsChartMarkers: [] as RegistrationChartMarker[]
      }
    }
    const minDate = source[0].date
    const maxDate = source[source.length - 1].date
    let baseRows = source.map(({ date, count }) => ({ date, count }))
    const visibleMarkers = chartMarkers.filter(({ date }) => {
      const inRange = date >= minDate && date <= maxDate
      if (!inRange) return false
      if (!baseRows.some((r) => r.date === date)) {
        baseRows.push({ date, count: 0 })
      }
      return true
    })
    baseRows.sort((a, b) => a.date.localeCompare(b.date))
    let cumulative = 0
    const cumulativeRows = baseRows.map(({ date, count }) => {
      cumulative += count
      return { date, count: cumulative, dailyCount: count }
    })
    return {
      assistanceActivationsByDateChartData: cumulativeRows,
      assistanceActivationsChartMarkers: visibleMarkers
    }
  }, [dashboardMetrics?.assistanceActivationsByDate, assistanceActivationsChartDays, chartMarkers])

  const statusPieColor = (name: string) => {
    if (name === 'Ativo') return STATUS_ATIVO_COLOR
    if (name === 'Inativo') return STATUS_INATIVO_COLOR
    if (name === 'Sem dados') return STATUS_SEM_DADOS_COLOR
    if (name === 'Cadastro parcial') return STATUS_CADASTRO_PARCIAL_COLOR
    return '#008B95'
  }

  const renderStatusSummaryItem = (label: string, value: number, color: string) => (
    <span style={{ color }}>
      {label}: {value}
    </span>
  )

  const COLORS = [STATUS_CADASTRO_PARCIAL_COLOR, STATUS_ATIVO_COLOR, STATUS_INATIVO_COLOR, STATUS_SEM_DADOS_COLOR]
  const TOOLTIP_CONTENT_STYLE = {
    backgroundColor: '#1f2937',
    border: 'none',
    borderRadius: '8px',
    color: '#f9fafb'
  } as const
  const TOOLTIP_ITEM_STYLE = { color: '#f9fafb', fontWeight: 500 } as const
  const TOOLTIP_LABEL_STYLE = { color: '#f9fafb', fontWeight: 600 } as const
  const TOOLTIP_WRAPPER_STYLE = { zIndex: 60 } as const

  if (error && !loading) {
    return (
      <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" color="muted">{error}</Text>
          </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard
            moduleName="superapp_dashboard"
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4 min-w-0">
                  <img
                    src="/Logo-super-app.png"
                    alt="Superapp"
                    className="h-14 w-auto flex-shrink-0 object-contain select-none"
                  />
                  <div className="min-w-0">
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      {moduleTitle}
                    </Heading>
                    <Text size="sm" color="muted" className="mt-2">
                      {moduleDescription}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Abas */}
              <div className="px-6">
                <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'dashboard' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => setActiveTab('usuarios')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'usuarios' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Usuários
                  </button>
                  <button
                    onClick={() => setActiveTab('promocoes')}
                    className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'promocoes' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    Promoções
                  </button>
                  {!milestonesPermissionsLoading && canAccessMomentosChaveTab ? (
                    <button
                      onClick={() => setActiveTab('momentos_chave')}
                      className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'momentos_chave' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                    >
                      Momentos-Chave
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Conteúdo por Aba */}
              {activeTab === 'dashboard' && (
                <div className="flex-1 px-6 py-4">
                  {dashboardLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : dashboardMetrics ? (
                    <>
                      {/* Métricas */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.totalUsers}
                            </Text>
                            <Text size="sm" className="text-white">
                              Total de Usuários
                            </Text>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <div className="grid grid-cols-2 gap-3 mb-2">
                              <div>
                                <Text size="3xl" className="text-white font-bold">
                                  {dashboardMetrics.usersByTipo.indicacao.toLocaleString('pt-BR')}
                                </Text>
                                <Text size="xs" className="text-indigo-100">
                                  Indicações
                                </Text>
                              </div>
                              <div>
                                <Text size="3xl" className="text-white font-bold">
                                  {dashboardMetrics.usersByTipo.segurado.toLocaleString('pt-BR')}
                                </Text>
                                <Text size="xs" className="text-indigo-100">
                                  Segurados
                                </Text>
                              </div>
                            </div>
                            <Text size="sm" className="text-white">
                              Usuários por Tipo
                            </Text>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.activeUsers}
                            </Text>
                            <div className="flex items-center justify-center gap-1 relative group">
                              <Text size="sm" className="text-white">
                                Usuários Ativos
                              </Text>
                              <InformationCircleIcon className="h-6 w-6 text-white cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-80 text-left">
                                  <div className="whitespace-normal">
                                    <div className="text-gray-300">
                                    <div className="mb-2">Cada usuário cadastrado conta uma vez: usamos o registro de comportamento mais recente do <span className="font-medium">user_id</span> (mesma regra da aba Usuários).</div>
                                    <div className="mb-1">Um usuário é considerado ativo quando a última atividade ocorreu nos últimos 30 dias.</div>
                                    <div>Usuários sem atividade por mais de 30 dias são automaticamente classificados como inativos.</div>
                                  </div>
                                </div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.atRiskUsers}
                            </Text>
                            <div className="flex items-center justify-center gap-1 relative group">
                              <Text size="sm" className="text-white">
                                Risco de Churn
                              </Text>
                              <InformationCircleIcon className="h-6 w-6 text-white cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-80 text-left">
                                <div className="whitespace-normal">
                                  <div className="font-semibold mb-2">Usuários identificados com risco de cancelamento ou abandono do SuperApp</div>
                                  <div className="text-gray-300 mt-2">
                                    <div className="mb-2">Contagem por usuário único (último snapshot de comportamento por <span className="font-medium">user_id</span>).</div>
                                    <div className="font-medium mb-1">Um usuário é classificado como em risco de churn quando ambas as condições abaixo são atendidas:</div>
                                    <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                                      <li>Última atividade realizada há mais de 14 dias</li>
                                      <li>Score de engajamento inferior a 30 pontos</li>
                                    </ul>
                                  </div>
                                </div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
                          <div className="text-center">
                            <Text size="3xl" className="text-white font-bold mb-2">
                              {dashboardMetrics.avgEngagementScore.toFixed(2)}
                            </Text>
                            <div className="flex items-center justify-center gap-1 relative group">
                              <Text size="sm" className="text-white">
                                Média do Score
                              </Text>
                              <InformationCircleIcon className="h-6 w-6 text-white cursor-help" />
                              <div className="absolute top-full right-0 mt-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-[36rem] sm:w-[40rem] text-left">
                                <div className="whitespace-normal">
                                  <div className="font-semibold mb-3 text-base">Score de Engajamento (0 a 100 pontos)</div>
                                  <div className="text-gray-300 mt-2">
                                    <div className="mb-3 text-gray-400">Média entre usuários com snapshot de comportamento (último registro por <span className="font-medium">user_id</span>).</div>
                                    <div className="mb-4">O score de engajamento é calculado automaticamente com base em quatro pilares:</div>
                                    <div className="space-y-4">
                                      <div>
                                        <div className="font-medium mb-2">1. Frequência de Eventos (até 30 pontos)</div>
                                        <div className="ml-2 mb-1">Considera o total de eventos registrados pelo usuário.</div>
                                        <div className="ml-2 mb-1">• Cálculo: total de eventos dividido por 10, limitado a 30 pontos.</div>
                                        <div className="ml-2">• Exemplo: 300 eventos = 30 pontos (pontuação máxima).</div>
                                      </div>
                                      <div>
                                        <div className="font-medium mb-2">2. Variedade de Ações (até 25 pontos)</div>
                                        <div className="ml-2 mb-1">Avalia a quantidade de tipos diferentes de ações realizadas nos últimos 30 dias.</div>
                                        <div className="ml-2 mb-1">• Cálculo: número de ações únicas multiplicado por 5, limitado a 25 pontos.</div>
                                        <div className="ml-2">• Exemplo: 5 tipos diferentes de ações = 25 pontos (pontuação máxima).</div>
                                      </div>
                                      <div>
                                        <div className="font-medium mb-2">3. Consistência de Uso (até 25 pontos)</div>
                                        <div className="ml-2 mb-1">Analisa a quantidade de dias distintos com atividade nos últimos 30 dias.</div>
                                        <div className="ml-2 mb-1">• Cálculo: número de dias únicos com atividade multiplicado por 2, limitado a 25 pontos.</div>
                                        <div className="ml-2">• Exemplo: 13 dias ou mais com atividade = 25 pontos (pontuação máxima).</div>
                                      </div>
                                      <div>
                                        <div className="font-medium mb-2">4. Recência da Última Atividade (até 20 pontos)</div>
                                        <div className="ml-2 mb-1">Baseada no intervalo desde a última atividade do usuário:</div>
                                        <div className="ml-2 mb-1">• Atividade realizada hoje: 20 pontos</div>
                                        <div className="ml-2 mb-1">• Entre 1 e 7 dias: 15 pontos</div>
                                        <div className="ml-2 mb-1">• Entre 8 e 14 dias: 10 pontos</div>
                                        <div className="ml-2">• Mais de 14 dias: 0 pontos</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="absolute bottom-full right-4 -mb-1">
                                  <div className="border-4 border-transparent border-b-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-3">
                        Indicadores pós lançamento oficial em 13 de Maio de 2026
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.newUsersSinceLaunch20260513.toLocaleString('pt-BR')}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block leading-snug">
                            Número de novos usuários
                          </Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.activeUsersSinceLaunch20260513.toLocaleString('pt-BR')}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block leading-snug">
                            Número de usuários com status ativo
                          </Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {usersWithoutActivationSinceLaunch.toLocaleString('pt-BR')}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block leading-snug">
                            Usuários sem ativação (Sem dados ou Cadastro parcial)
                          </Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-1.5">
                            <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                              {postLaunchDesistenciaPercents.sobreNovos === null
                                ? '—'
                                : `${postLaunchDesistenciaPercents.sobreNovos.toFixed(1)}%`}
                            </Text>
                            <ExclamationCircleIcon
                              className="h-7 w-7 shrink-0 text-red-600 dark:text-red-500"
                              aria-hidden
                            />
                          </div>
                          <Text size="sm" color="muted" className="mt-1 block leading-snug">
                            % de usuários sem ativação (Sem dados ou Cadastro parcial)
                          </Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.avgNewUsersPerDaySinceLaunch20260513.toLocaleString('pt-BR', {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block leading-snug">
                            Média de novos usuários por dia
                          </Text>
                        </div>
                      </div>

                      <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-3">
                        Indicadores complementares
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.usersWithBehaviorRecords.toLocaleString('pt-BR')}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block leading-snug">
                            Usuários com registros de comportamento
                          </Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.churnRiskRatePercent.toFixed(1)}%
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block">Taxa de risco de abandono (sobre quem tem último registro de comportamento)</Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.avgDaysSinceLastActivity.toFixed(1)}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block">Média de dias desde a última atividade</Text>
                        </div>
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 border border-gray-200 dark:border-gray-700">
                          <Text size="2xl" weight="bold" className="text-gray-900 dark:text-white tabular-nums">
                            {dashboardMetrics.avgCustomerLifetimeDays.toFixed(1)}
                          </Text>
                          <Text size="sm" color="muted" className="mt-1 block">Tempo médio de permanência na plataforma (dias)</Text>
                        </div>
                      </div>

                      {/* Gráficos por data — abas */}
                      <div className="mb-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
                            <nav
                              className="-mb-px flex flex-wrap gap-x-1 border-b border-gray-200 dark:border-gray-700 w-full sm:w-auto"
                              aria-label="Gráficos por data"
                            >
                              <button
                                type="button"
                                onClick={() => setDashboardChartTab('registrations')}
                                className={`px-4 py-2 -mb-px border-b-2 text-sm sm:text-base whitespace-nowrap ${dashboardChartTab === 'registrations' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                              >
                                Usuários Cadastrados por Data
                              </button>
                              <button
                                type="button"
                                onClick={() => setDashboardChartTab('lastAccess')}
                                className={`px-4 py-2 -mb-px border-b-2 text-sm sm:text-base whitespace-nowrap ${dashboardChartTab === 'lastAccess' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                              >
                                Usuários por Data de Último Acesso
                              </button>
                              <button
                                type="button"
                                onClick={() => setDashboardChartTab('assistanceActivations')}
                                className={`px-4 py-2 -mb-px border-b-2 text-sm sm:text-base whitespace-nowrap ${dashboardChartTab === 'assistanceActivations' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                              >
                                Acionamentos de Assistências
                              </button>
                            </nav>
                            <div className="flex items-center gap-2 shrink-0">
                              <Select
                                size="sm"
                                className="!w-auto min-w-[11rem]"
                                value={
                                  dashboardChartTab === 'registrations'
                                    ? registrationsChartDays
                                    : dashboardChartTab === 'lastAccess'
                                      ? lastAccessChartDays
                                      : assistanceActivationsChartDays
                                }
                                onChange={(e) => {
                                  const days = Number(e.target.value) as SuperappDashboardChartDays
                                  if (dashboardChartTab === 'registrations') {
                                    setRegistrationsChartDays(days)
                                  } else if (dashboardChartTab === 'lastAccess') {
                                    setLastAccessChartDays(days)
                                  } else {
                                    setAssistanceActivationsChartDays(days)
                                  }
                                }}
                                aria-label={
                                  dashboardChartTab === 'registrations'
                                    ? 'Período do gráfico de cadastros'
                                    : dashboardChartTab === 'lastAccess'
                                      ? 'Período do gráfico de último acesso'
                                      : 'Período do gráfico de acionamentos'
                                }
                              >
                                <option value={90}>Últimos 90 dias</option>
                                <option value={60}>Últimos 60 dias</option>
                                <option value={30}>Últimos 30 dias</option>
                              </Select>
                            </div>
                          </div>

                          {dashboardChartTab === 'registrations' ? (
                            <>
                          <Text size="sm" color="muted" className="mb-4 block">
                            Barras: total de cadastros no dia. Linhas: distribuição por status atual (Ativo, Inativo, Sem dados ou Cadastro parcial).
                          </Text>
                          <div className="h-[32rem] min-h-[32rem] w-full min-w-0 text-slate-600 dark:text-slate-300 [&_.recharts-legend-item-text]:fill-current [&_.recharts-cartesian-axis-tick-value]:fill-current">
                            {registrationsByDateChartData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%" minHeight={512}>
                                <ComposedChart
                                  data={registrationsByDateChartData}
                                  margin={{
                                    top: registrationChartMarkers.length > 0 ? 36 : 28,
                                    right: 28,
                                    left: 8,
                                    bottom: 4
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.35} />
                                  <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12, fill: 'currentColor' }}
                                    axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickFormatter={formatDateShort}
                                    interval="preserveStartEnd"
                                    minTickGap={24}
                                  />
                                  <YAxis
                                    tick={{ fontSize: 12, fill: 'currentColor' }}
                                    axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    allowDecimals={false}
                                    width={44}
                                    domain={[0, (dataMax: number) => Math.max(5, Math.ceil(dataMax * 1.1) || 5)]}
                                  />
                                  <Tooltip
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload?.length) return null
                                      const row = payload[0]?.payload as {
                                        ativo: number
                                        inativo: number
                                        semDados: number
                                        cadastroParcial: number
                                        count: number
                                      }
                                      if (!row) return null
                                      const totalDia =
                                        row.count ??
                                        row.ativo + row.inativo + row.semDados + row.cadastroParcial
                                      return (
                                        <div
                                          className="rounded-lg px-3 py-2 text-sm shadow-lg"
                                          style={{
                                            ...TOOLTIP_CONTENT_STYLE,
                                            padding: '10px 12px'
                                          }}
                                        >
                                          <div style={TOOLTIP_LABEL_STYLE}>
                                            {(() => {
                                              const dateLabel = String(label)
                                              const weekday = formatWeekdayName(dateLabel)
                                              return (
                                                <>
                                                  Data: {formatDateShort(dateLabel)}
                                                  {weekday ? ` · ${weekday}` : ''}
                                                </>
                                              )
                                            })()}
                                          </div>
                                          <div className="mt-2 space-y-0.5" style={{ color: '#e5e7eb' }}>
                                            {row.ativo > 0 ? (
                                              <div style={{ color: STATUS_ATIVO_COLOR }}>Ativo: {row.ativo}</div>
                                            ) : null}
                                            {row.inativo > 0 ? (
                                              <div style={{ color: STATUS_INATIVO_COLOR }}>Inativo: {row.inativo}</div>
                                            ) : null}
                                            {row.semDados > 0 ? (
                                              <div style={{ color: STATUS_SEM_DADOS_COLOR }}>
                                                Sem dados: {row.semDados}
                                              </div>
                                            ) : null}
                                            {row.cadastroParcial > 0 ? (
                                              <div style={{ color: STATUS_CADASTRO_PARCIAL_COLOR }}>
                                                Cadastro parcial: {row.cadastroParcial}
                                              </div>
                                            ) : null}
                                          </div>
                                          <div
                                            className="mt-2 border-t border-gray-600 pt-2"
                                            style={{ ...TOOLTIP_LABEL_STYLE, fontSize: '0.8125rem' }}
                                          >
                                            Total do dia: {totalDia}
                                          </div>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Bar
                                    dataKey="count"
                                    name="Total do dia"
                                    fill="#6366f1"
                                    fillOpacity={0.22}
                                    stroke="#6366f1"
                                    strokeOpacity={0.45}
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={48}
                                    isAnimationActive={false}
                                  >
                                    <LabelList
                                      dataKey="count"
                                      position="top"
                                      offset={6}
                                      fill="#4f46e5"
                                      className="dark:[fill:#a5b4fc]"
                                      fontSize={11}
                                      fontWeight={600}
                                      formatter={(label) => {
                                        const n = typeof label === 'number' ? label : Number(label)
                                        if (!Number.isFinite(n) || n <= 0) return ''
                                        return String(n)
                                      }}
                                    />
                                  </Bar>
                                  {registrationChartMarkers.map((marker) => (
                                    <ReferenceLine
                                      key={marker.id}
                                      x={marker.date}
                                      stroke={marker.color}
                                      strokeWidth={2}
                                      strokeDasharray="6 4"
                                      label={{
                                        value: marker.legendLabel,
                                        position: 'top',
                                        fill: marker.color,
                                        fontSize: 11,
                                        fontWeight: 600
                                      }}
                                    />
                                  ))}
                                  {showRegistrationLines.ativo ? (
                                    <Line
                                      type="monotone"
                                      dataKey="ativoLine"
                                      name="Ativo"
                                      stroke={STATUS_ATIVO_COLOR}
                                      strokeWidth={3}
                                      connectNulls={false}
                                      dot={{ r: 4, fill: STATUS_ATIVO_COLOR, stroke: '#f8fafc', strokeWidth: 1 }}
                                      activeDot={{ r: 6, fill: STATUS_ATIVO_COLOR, stroke: '#f8fafc', strokeWidth: 2 }}
                                      isAnimationActive={false}
                                    />
                                  ) : null}
                                  {showRegistrationLines.inativo ? (
                                    <Line
                                      type="monotone"
                                      dataKey="inativoLine"
                                      name="Inativo"
                                      stroke={STATUS_INATIVO_COLOR}
                                      strokeWidth={3}
                                      connectNulls={false}
                                      dot={{ r: 4, fill: STATUS_INATIVO_COLOR, stroke: '#f8fafc', strokeWidth: 1 }}
                                      activeDot={{ r: 6, fill: STATUS_INATIVO_COLOR, stroke: '#f8fafc', strokeWidth: 2 }}
                                      isAnimationActive={false}
                                    />
                                  ) : null}
                                  {showRegistrationLines.semDados ? (
                                    <Line
                                      type="monotone"
                                      dataKey="semDadosLine"
                                      name="Sem dados"
                                      stroke={STATUS_SEM_DADOS_COLOR}
                                      strokeWidth={3}
                                      connectNulls={false}
                                      dot={{ r: 4, fill: STATUS_SEM_DADOS_COLOR, stroke: '#f8fafc', strokeWidth: 1 }}
                                      activeDot={{ r: 6, fill: STATUS_SEM_DADOS_COLOR, stroke: '#f8fafc', strokeWidth: 2 }}
                                      isAnimationActive={false}
                                    />
                                  ) : null}
                                  {showRegistrationLines.cadastroParcial ? (
                                    <Line
                                      type="monotone"
                                      dataKey="cadastroParcialLine"
                                      name="Cadastro parcial"
                                      stroke={STATUS_CADASTRO_PARCIAL_COLOR}
                                      strokeWidth={3}
                                      connectNulls={false}
                                      dot={{ r: 4, fill: STATUS_CADASTRO_PARCIAL_COLOR, stroke: '#f8fafc', strokeWidth: 1 }}
                                      activeDot={{ r: 6, fill: STATUS_CADASTRO_PARCIAL_COLOR, stroke: '#f8fafc', strokeWidth: 2 }}
                                      isAnimationActive={false}
                                    />
                                  ) : null}
                                  <Legend
                                    verticalAlign="bottom"
                                    wrapperStyle={{ paddingTop: 8, paddingBottom: 0, marginBottom: 0 }}
                                    content={(props) =>
                                      renderRegistrationChartLegend(props, registrationChartMarkers)
                                    }
                                  />
                                </ComposedChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Sem dados para o período selecionado</Text>
                              </div>
                            )}
                          </div>
                            </>
                          ) : dashboardChartTab === 'lastAccess' ? (
                            <>
                          <Text size="sm" color="muted" className="mb-4 block">
                            Barras: usuários ativos cuja última atividade registrada foi no dia. Linha tracejada: tendência preditiva (regressão linear desde o lançamento). Linha contínua laranja: média diária no mesmo período.
                          </Text>
                          <div className="h-[32rem] min-h-[32rem] w-full min-w-0 text-slate-600 dark:text-slate-300 [&_.recharts-legend-item-text]:fill-current [&_.recharts-cartesian-axis-tick-value]:fill-current">
                            {lastAccessByDateChartData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%" minHeight={512}>
                                <ComposedChart
                                  data={lastAccessByDateChartData}
                                  margin={{ top: lastAccessChartMarkers.length > 0 ? 36 : 28, right: 28, left: 8, bottom: 4 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.35} />
                                  <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12, fill: 'currentColor' }}
                                    axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickFormatter={formatDateShort}
                                    interval="preserveStartEnd"
                                    minTickGap={24}
                                  />
                                  <YAxis
                                    tick={{ fontSize: 12, fill: 'currentColor' }}
                                    axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    allowDecimals={false}
                                    width={44}
                                    domain={[0, (dataMax: number) => Math.max(5, Math.ceil(dataMax * 1.1) || 5)]}
                                  />
                                  <Tooltip
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload?.length) return null
                                      const row = payload[0]?.payload as {
                                        count: number
                                        trendLine?: number | null
                                        avgLine?: number | null
                                      }
                                      if (!row) return null
                                      return (
                                        <div
                                          className="rounded-lg px-3 py-2 text-sm shadow-lg"
                                          style={{
                                            ...TOOLTIP_CONTENT_STYLE,
                                            padding: '10px 12px'
                                          }}
                                        >
                                          <div style={TOOLTIP_LABEL_STYLE}>
                                            {(() => {
                                              const dateLabel = String(label)
                                              const weekday = formatWeekdayName(dateLabel)
                                              return (
                                                <>
                                                  Último acesso: {formatDateShort(dateLabel)}
                                                  {weekday ? ` · ${weekday}` : ''}
                                                </>
                                              )
                                            })()}
                                          </div>
                                          <div className="mt-2 space-y-0.5" style={{ color: '#e5e7eb' }}>
                                            <div>Usuários ativos: {row.count}</div>
                                            {row.trendLine != null ? (
                                              <div>Tendência: {row.trendLine}</div>
                                            ) : null}
                                            {row.avgLine != null ? (
                                              <div>Média desde lançamento: {row.avgLine}</div>
                                            ) : null}
                                          </div>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Bar
                                    dataKey="count"
                                    name="Usuários ativos"
                                    fill="#0d9488"
                                    fillOpacity={0.22}
                                    stroke="#0d9488"
                                    strokeOpacity={0.45}
                                    radius={[4, 4, 0, 0]}
                                    maxBarSize={48}
                                    isAnimationActive={false}
                                  >
                                    <LabelList
                                      dataKey="count"
                                      position="top"
                                      offset={6}
                                      fill="#0f766e"
                                      className="dark:[fill:#5eead4]"
                                      fontSize={11}
                                      fontWeight={600}
                                      formatter={(label) => {
                                        const n = typeof label === 'number' ? label : Number(label)
                                        if (!Number.isFinite(n) || n <= 0) return ''
                                        return String(n)
                                      }}
                                    />
                                  </Bar>
                                  {hasLastAccessTrendLine ? (
                                    <Line
                                      type="monotone"
                                      dataKey="trendLine"
                                      name="Tendência preditiva"
                                      stroke="#0f766e"
                                      strokeWidth={2.5}
                                      strokeDasharray="8 4"
                                      connectNulls
                                      dot={false}
                                      activeDot={{ r: 4, fill: '#0f766e', stroke: '#f8fafc', strokeWidth: 2 }}
                                      isAnimationActive={false}
                                    />
                                  ) : null}
                                  {hasLastAccessAvgLine ? (
                                    <Line
                                      type="monotone"
                                      dataKey="avgLine"
                                      name="Média desde lançamento"
                                      stroke="#d97706"
                                      strokeWidth={2.5}
                                      connectNulls
                                      dot={false}
                                      activeDot={{ r: 4, fill: '#d97706', stroke: '#f8fafc', strokeWidth: 2 }}
                                      isAnimationActive={false}
                                    />
                                  ) : null}
                                  <Legend
                                    verticalAlign="bottom"
                                    wrapperStyle={{ paddingTop: 8, paddingBottom: 0, marginBottom: 0 }}
                                    content={(props) =>
                                      renderRegistrationChartLegend(props, lastAccessChartMarkers)
                                    }
                                  />
                                  {lastAccessChartMarkers.map((marker) => (
                                    <ReferenceLine
                                      key={marker.id}
                                      x={marker.date}
                                      stroke={marker.color}
                                      strokeWidth={2}
                                      strokeDasharray="6 4"
                                      label={{
                                        value: marker.legendLabel,
                                        position: 'top',
                                        fill: marker.color,
                                        fontSize: 11,
                                        fontWeight: 600
                                      }}
                                    />
                                  ))}
                                </ComposedChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Sem dados para o período selecionado</Text>
                              </div>
                            )}
                          </div>
                            </>
                          ) : (
                            <>
                          <Text size="sm" color="muted" className="mb-4 block">
                            Linha principal: total acumulado no período. Linha cinza: acionamentos no dia.
                          </Text>
                          <div className="h-[32rem] min-h-[32rem] w-full min-w-0 text-slate-600 dark:text-slate-300 [&_.recharts-legend-item-text]:fill-current [&_.recharts-cartesian-axis-tick-value]:fill-current">
                            {assistanceActivationsByDateChartData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%" minHeight={512}>
                                <ComposedChart
                                  data={assistanceActivationsByDateChartData}
                                  margin={{
                                    top: assistanceActivationsChartMarkers.length > 0 ? 36 : 28,
                                    right: 28,
                                    left: 8,
                                    bottom: 4
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.35} />
                                  <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12, fill: 'currentColor' }}
                                    axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickFormatter={formatDateShort}
                                    interval="preserveStartEnd"
                                    minTickGap={24}
                                  />
                                  <YAxis
                                    tick={{ fontSize: 12, fill: 'currentColor' }}
                                    axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                                    allowDecimals={false}
                                    width={44}
                                    domain={[0, (dataMax: number) => Math.max(5, Math.ceil(dataMax * 1.1) || 5)]}
                                  />
                                  <Tooltip
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload?.length) return null
                                      const row = payload[0]?.payload as {
                                        count: number
                                        dailyCount?: number
                                      }
                                      if (!row) return null
                                      return (
                                        <div
                                          className="rounded-lg px-3 py-2 text-sm shadow-lg"
                                          style={{
                                            ...TOOLTIP_CONTENT_STYLE,
                                            padding: '10px 12px'
                                          }}
                                        >
                                          <div style={TOOLTIP_LABEL_STYLE}>
                                            {(() => {
                                              const dateLabel = String(label)
                                              const weekday = formatWeekdayName(dateLabel)
                                              return (
                                                <>
                                                  {formatDateShort(dateLabel)}
                                                  {weekday ? ` · ${weekday}` : ''}
                                                </>
                                              )
                                            })()}
                                          </div>
                                          <div className="mt-2 space-y-0.5" style={{ color: '#e5e7eb' }}>
                                            <div>Acumulado: {row.count}</div>
                                            {row.dailyCount != null ? (
                                              <div>No dia: {row.dailyCount}</div>
                                            ) : null}
                                          </div>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="count"
                                    name="Acumulado"
                                    stroke="#008B95"
                                    strokeWidth={2.5}
                                    connectNulls
                                    dot={{ r: 3, fill: '#008B95', stroke: '#f8fafc', strokeWidth: 1 }}
                                    activeDot={{ r: 5, fill: '#008B95', stroke: '#f8fafc', strokeWidth: 2 }}
                                    isAnimationActive={false}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="dailyCount"
                                    name="No dia"
                                    stroke="#9ca3af"
                                    strokeWidth={2}
                                    connectNulls
                                    dot={{ r: 2.5, fill: '#9ca3af', stroke: '#f8fafc', strokeWidth: 1 }}
                                    activeDot={{ r: 4, fill: '#9ca3af', stroke: '#f8fafc', strokeWidth: 2 }}
                                    isAnimationActive={false}
                                  />
                                  <Legend
                                    verticalAlign="bottom"
                                    wrapperStyle={{ paddingTop: 8, paddingBottom: 0, marginBottom: 0 }}
                                    content={(props) =>
                                      renderRegistrationChartLegend(props, assistanceActivationsChartMarkers)
                                    }
                                  />
                                  {assistanceActivationsChartMarkers.map((marker) => (
                                    <ReferenceLine
                                      key={marker.id}
                                      x={marker.date}
                                      stroke={marker.color}
                                      strokeWidth={2}
                                      strokeDasharray="6 4"
                                      label={{
                                        value: marker.legendLabel,
                                        position: 'top',
                                        fill: marker.color,
                                        fontSize: 11,
                                        fontWeight: 600
                                      }}
                                    />
                                  ))}
                                </ComposedChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Sem dados para o período selecionado</Text>
                              </div>
                            )}
                          </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Top 20 empresas por usuários */}
                      <div className="mb-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
                            Top 20 empresas por usuários
                          </Heading>
                          <Text size="sm" color="muted" className="mb-4 block">
                            Empresas com mais usuários cadastrados na base (até 20), com quantidade e percentual por status em relação ao total da empresa.
                          </Text>
                          {dashboardMetrics.topCompaniesByUsers.length > 0 ? (
                            <div className="max-h-96 overflow-y-auto overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/80 sticky top-0 z-10">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300 w-10">
                                      #
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                      Empresa
                                    </th>
                                    <th
                                      className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums"
                                      style={{ color: STATUS_ATIVO_COLOR }}
                                    >
                                      Ativo
                                    </th>
                                    <th
                                      className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums"
                                      style={{ color: STATUS_INATIVO_COLOR }}
                                    >
                                      Inativo
                                    </th>
                                    <th
                                      className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums"
                                      style={{ color: STATUS_SEM_DADOS_COLOR }}
                                    >
                                      Sem dados
                                    </th>
                                    <th
                                      className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums"
                                      style={{ color: STATUS_CADASTRO_PARCIAL_COLOR }}
                                    >
                                      Cad. parcial
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300 tabular-nums">
                                      Total
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                  {dashboardMetrics.topCompaniesByUsers.map((item, index) => (
                                    <tr
                                      key={item.company}
                                      className="even:bg-gray-50 dark:even:bg-gray-800/80"
                                    >
                                      <td className="px-4 py-3 text-sm font-semibold text-gray-500 dark:text-gray-400 tabular-nums">
                                        {index + 1}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-xs truncate"
                                        title={item.company}
                                      >
                                        {item.company}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-sm text-right tabular-nums"
                                        style={{ color: STATUS_ATIVO_COLOR }}
                                      >
                                        {formatCountWithRowPercent(item.ativo, item.count)}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-sm text-right tabular-nums"
                                        style={{ color: STATUS_INATIVO_COLOR }}
                                      >
                                        {formatCountWithRowPercent(item.inativo, item.count)}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-sm text-right tabular-nums"
                                        style={{ color: STATUS_SEM_DADOS_COLOR }}
                                      >
                                        {formatCountWithRowPercent(item.semDados, item.count)}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-sm text-right tabular-nums"
                                        style={{ color: STATUS_CADASTRO_PARCIAL_COLOR }}
                                      >
                                        {formatCountWithRowPercent(item.cadastroParcial, item.count)}
                                      </td>
                                      <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 text-right tabular-nums">
                                        {item.count}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center py-16">
                              <Text size="sm" color="muted">Nenhum dado disponível</Text>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Gráficos */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        {/* Gráfico de Pizza - Distribuição por Status */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Distribuição de Usuários por Status
                          </Heading>
                          <Text size="sm" color="muted" className="mb-2 block">
                            Ativo e Inativo refletem o último snapshot válido; Sem dados e Cadastro parcial são usuários sem snapshot válido (com ou sem e-mail/telefone).
                          </Text>
                          <Text size="sm" className="mb-3 block text-gray-800 dark:text-gray-200 font-medium">
                            {renderStatusSummaryItem('Ativos', statusDistributionSummary.ativo, STATUS_ATIVO_COLOR)}
                            {' · '}
                            {renderStatusSummaryItem('Inativos', statusDistributionSummary.inativo, STATUS_INATIVO_COLOR)}
                            {' · '}
                            {renderStatusSummaryItem('Sem dados', statusDistributionSummary.semDados, STATUS_SEM_DADOS_COLOR)}
                            {' · '}
                            {renderStatusSummaryItem(
                              'Cadastro parcial',
                              statusDistributionSummary.cadastroParcial,
                              STATUS_CADASTRO_PARCIAL_COLOR
                            )}
                          </Text>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieChartData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, value, percent }) =>
                                    `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`
                                  }
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {pieChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={statusPieColor(String(entry.name))} />
                                  ))}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                  />
                                  <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Gráfico de Barras - Módulo Favorito */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Módulo Favorito
                          </Heading>
                          <div className="h-64">
                            {dashboardMetrics.favoriteModules.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={dashboardMetrics.favoriteModules.map(item => ({
                                    name: item.module,
                                    value: item.count
                                  }))}
                                  layout="vertical"
                                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                  <XAxis type="number" className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <YAxis type="category" dataKey="name" width={75} className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    formatter={(value) => [Number(value), 'Usuários']}
                                    labelFormatter={(label) => `Módulo: ${label}`}
                                  />
                                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                                    {dashboardMetrics.favoriteModules.map((_, index) => (
                                      <Cell key={`module-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Nenhum dado disponível</Text>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Gráfico de Pizza - Horário Preferido */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Horário Preferido
                          </Heading>
                          <div className="h-64">
                            {dashboardMetrics.preferredTimeSlots.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={dashboardMetrics.preferredTimeSlots.map(item => ({
                                      name: item.timeSlot,
                                      value: item.count
                                    }))}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                  >
                                    {dashboardMetrics.preferredTimeSlots.map((entry, index) => (
                                      <Cell key={`timeslot-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                  />
                                  <Legend />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Nenhum dado disponível</Text>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Gráfico de Barras - Usuários por Cidade (mesma linha que Horário Preferido em lg) */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 min-w-0">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Usuários por Cidade
                          </Heading>
                          <div style={{ height: usersByCityChartHeight }} className="min-h-[17.5rem] w-full">
                            {dashboardMetrics.usersByCity.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={dashboardMetrics.usersByCity}
                                  layout="vertical"
                                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                                  barCategoryGap="20%"
                                >
                                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                  <XAxis type="number" allowDecimals={false} className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <YAxis
                                    type="category"
                                    dataKey="city"
                                    width={132}
                                    interval={0}
                                    className="text-xs"
                                    tick={{ fill: 'currentColor', fontSize: 10 }}
                                    tickFormatter={(city) =>
                                      city.length > 28 ? `${city.slice(0, 28)}…` : city
                                    }
                                  />
                                  <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    formatter={(value) => [Number(value), 'Usuários']}
                                    labelFormatter={(label) => String(label)}
                                  />
                                  <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={32} fill="#6366f1" />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Nenhum dado disponível</Text>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-3 mt-2">
                        Indicadores estratégicos
                      </Heading>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Faixas de engajamento (por usuário)
                          </Heading>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={[
                                  { name: 'Baixo (0–33)', value: dashboardMetrics.engagementBands.low },
                                  { name: 'Médio (34–66)', value: dashboardMetrics.engagementBands.medium },
                                  { name: 'Alto (67–100)', value: dashboardMetrics.engagementBands.high }
                                ]}
                                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6b7280" interval={0} angle={-12} textAnchor="end" height={48} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#6b7280" />
                                <Tooltip
                                  contentStyle={TOOLTIP_CONTENT_STYLE}
                                  itemStyle={TOOLTIP_ITEM_STYLE}
                                  labelStyle={TOOLTIP_LABEL_STYLE}
                                  wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                  formatter={(value) => [`${Number(value)} usuário(s)`, 'Quantidade']}
                                />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#6366f1" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Ciclo de vida na plataforma (dias)
                          </Heading>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={[
                                  { name: 'Novos ≤30', value: dashboardMetrics.lifecycleBuckets.newUsers },
                                  { name: '31–90', value: dashboardMetrics.lifecycleBuckets.developing },
                                  { name: '90+', value: dashboardMetrics.lifecycleBuckets.mature }
                                ]}
                                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#6b7280" />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#6b7280" />
                                <Tooltip
                                  contentStyle={TOOLTIP_CONTENT_STYLE}
                                  itemStyle={TOOLTIP_ITEM_STYLE}
                                  labelStyle={TOOLTIP_LABEL_STYLE}
                                  wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                  formatter={(value) => [`${Number(value)} usuário(s)`, 'Quantidade']}
                                />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#0d9488" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 lg:col-span-2">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Score médio por faixa de tempo de vida (correlação)
                          </Heading>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={dashboardMetrics.lifecycleEngagement} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                <XAxis dataKey="bucket" tick={{ fontSize: 10 }} stroke="#6b7280" interval={0} angle={-18} textAnchor="end" height={70} />
                                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#6b7280" />
                                  <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    formatter={(value, _name, item) => {
                                      const n = (item?.payload as { count?: number })?.count ?? 0
                                      return [`${Number(value).toFixed(2)} (base: ${n} usuários)`, 'Score médio']
                                    }}
                                  />
                                <Bar dataKey="avgScore" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="avgScore" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 lg:col-span-2">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Última ação mais frequente (top)
                          </Heading>
                          <div className="h-80">
                            {dashboardMetrics.lastActionTop.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={dashboardMetrics.lastActionTop.map((item) => ({
                                    name: item.action.length > 40 ? `${item.action.slice(0, 40)}…` : item.action,
                                    full: item.action,
                                    value: item.count
                                  }))}
                                  layout="vertical"
                                  margin={{ top: 5, right: 24, left: 8, bottom: 5 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-600" />
                                  <XAxis type="number" className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <YAxis type="category" dataKey="name" width={200} className="text-xs" tick={{ fill: 'currentColor' }} />
                                  <Tooltip
                                    contentStyle={TOOLTIP_CONTENT_STYLE}
                                    itemStyle={TOOLTIP_ITEM_STYLE}
                                    labelStyle={TOOLTIP_LABEL_STYLE}
                                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                                    formatter={(value) => [Number(value), 'Usuários']}
                                    labelFormatter={(_label, payload) =>
                                      (Array.isArray(payload) && payload[0] && (payload[0].payload as { full?: string }).full) || ''
                                    }
                                  />
                                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={26} fill="#ea580c" />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Text size="sm" color="muted">Nenhum dado disponível</Text>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Qualidade do cadastro
                          </Heading>
                          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                            <div className="flex justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
                              <span>Sem e-mail</span>
                              <span className="font-semibold tabular-nums">
                                {dashboardMetrics.cadastroQuality.noEmailCount} ({dashboardMetrics.cadastroQuality.noEmailPercent.toFixed(1)}%)
                              </span>
                            </div>
                            <div className="flex justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
                              <span>Sem telefone</span>
                              <span className="font-semibold tabular-nums">
                                {dashboardMetrics.cadastroQuality.noPhoneCount} ({dashboardMetrics.cadastroQuality.noPhonePercent.toFixed(1)}%)
                              </span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span>Sem localização no snapshot</span>
                              <span className="font-semibold tabular-nums">
                                {dashboardMetrics.cadastroQuality.noLocationCount} ({dashboardMetrics.cadastroQuality.noLocationPercent.toFixed(1)}%)
                              </span>
                            </div>
                            <Text size="xs" color="muted" className="pt-2 block">
                              Percentuais sobre o total de usuários cadastrados. Localização ausente inclui cadastros sem snapshot ou sem cidade/coordenadas válidas.
                            </Text>
                          </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                            Perfil de usuários em risco (churn)
                          </Heading>
                          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                            <div className="flex justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
                              <span>Em risco com score = 0</span>
                              <span className="font-semibold tabular-nums">{dashboardMetrics.riskProfile.atRiskZeroEngagement}</span>
                            </div>
                            <div className="flex justify-between gap-2 border-b border-gray-100 dark:border-gray-700 pb-2">
                              <span>Em risco com 0 eventos</span>
                              <span className="font-semibold tabular-nums">{dashboardMetrics.riskProfile.atRiskZeroEvents}</span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span>Em risco sem localização</span>
                              <span className="font-semibold tabular-nums">{dashboardMetrics.riskProfile.atRiskNoLocation}</span>
                            </div>
                            <Text size="xs" color="muted" className="pt-2 block">
                              Subconjuntos entre usuários com <span className="font-medium">risk_churn</span> no último snapshot.
                            </Text>
                          </div>
                        </div>
                      </div>

                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <Text size="lg" color="muted">Nenhum dado disponível</Text>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'usuarios' && (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {/* Filtros */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                        Filtros
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Nome
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o nome..."
                            value={filtroNomeUsuario}
                            onChange={(e) => setFiltroNomeUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Email
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o email..."
                            value={filtroEmailUsuario}
                            onChange={(e) => setFiltroEmailUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            CPF
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o CPF..."
                            value={filtroCpfUsuario}
                            onChange={(e) => setFiltroCpfUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Telefone
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o telefone..."
                            value={filtroTelefoneUsuario}
                            onChange={(e) => setFiltroTelefoneUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Empresa
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite a empresa..."
                            value={filtroEmpresaUsuario}
                            onChange={(e) => setFiltroEmpresaUsuario(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Criação (de)
                          </Text>
                          <Input
                            type="date"
                            value={filtroCriacaoInicio}
                            onChange={(e) => setFiltroCriacaoInicio(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Criação (até)
                          </Text>
                          <Input
                            type="date"
                            value={filtroCriacaoFim}
                            onChange={(e) => setFiltroCriacaoFim(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Status
                          </Text>
                          <select
                            value={filtroStatus}
                            onChange={(e) =>
                              setFiltroStatus(
                                (e.target.value || '') as
                                  | ''
                                  | 'ativo'
                                  | 'inativo'
                                  | 'sem_dados'
                                  | 'cadastro_parcial'
                              )
                            }
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                          >
                            <option value="">Todos</option>
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                            <option value="sem_dados">Sem dados</option>
                            <option value="cadastro_parcial">Cadastro Parcial</option>
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Risco de churn
                          </Text>
                          <select
                            value={filtroRiscoChurn}
                            onChange={(e) => setFiltroRiscoChurn((e.target.value || '') as '' | 'sim' | 'nao')}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                          >
                            <option value="">Todos</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Tipo
                          </Text>
                          <select
                            value={filtroTipo}
                            onChange={(e) => setFiltroTipo((e.target.value || '') as '' | 'segurado' | 'indicacao')}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                          >
                            <option value="">Todos</option>
                            <option value="segurado">Segurado</option>
                            <option value="indicacao">Indicação</option>
                          </select>
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Acionamento assistências
                          </Text>
                          <select
                            value={filtroAcionamentoAssistencias}
                            onChange={(e) =>
                              setFiltroAcionamentoAssistencias((e.target.value || '') as '' | 'com' | 'sem')
                            }
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                          >
                            <option value="">Todos</option>
                            <option value="com">Com acionamento</option>
                            <option value="sem">Sem acionamento</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFiltroNomeUsuario('')
                              setFiltroEmailUsuario('')
                              setFiltroCpfUsuario('')
                              setFiltroTelefoneUsuario('')
                              setFiltroEmpresaUsuario('')
                              setFiltroCriacaoInicio('')
                              setFiltroCriacaoFim('')
                              setFiltroStatus('')
                              setFiltroRiscoChurn('')
                              setFiltroTipo('')
                              setFiltroAcionamentoAssistencias('')
                            }}
                          >
                            Limpar Filtros
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                      <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                        Usuários
                        <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                          ({usersFiltrados.length} registros)
                        </span>
                      </Heading>
                      {canDownloadInfo && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={exportUsersExcel}
                          disabled={loading || usersFiltrados.length === 0}
                          title={
                            usersFiltrados.length === 0
                              ? 'Nenhum registro para exportar'
                              : 'Baixar Excel com a relação filtrada'
                          }
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                          Baixar Excel
                        </Button>
                      )}
                    </div>

                    {/* Tabela */}
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : (
                      <>
                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => scrollUsersBy(-320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a esquerda"
                          >
                            <ChevronLeftIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollUsersBy(320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a direita"
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                          <div ref={usersScrollRef} className="overflow-x-auto" onWheel={handleUsersWheel}>
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <SortableTableHeader
                                    sortKey="name"
                                    currentSort={sortByUser}
                                    sortOrder={sortOrderUser}
                                    onSort={toggleSortUser}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300 w-48 sticky left-0 z-20 bg-gray-50 dark:bg-gray-700"
                                  >
                                    Nome
                                  </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="company"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300 min-w-[18rem] w-[18rem]"
                                >
                                  Empresa
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="cpf"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  CPF
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="email"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Email
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="phone"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Telefone
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="tipo"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Tipo
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="status"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Status
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="acionamento_assistencias"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Acionamento assistências
                                </SortableTableHeader>
                                <SortableTableHeader
                                  sortKey="created_at"
                                  currentSort={sortByUser}
                                  sortOrder={sortOrderUser}
                                  onSort={toggleSortUser}
                                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                >
                                  Data de Criação
                                </SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {usersPaginados.length === 0 ? (
                                <tr>
                                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                    Nenhum usuário encontrado
                                  </td>
                                </tr>
                              ) : (
                                usersPaginados.map((user) => {
                                  const indicationRef = getUserIndicationRef(user.cpf, appIndicationByCpf)
                                  const userTipo = indicationRef ? 'Indicação' : 'Segurado'
                                  return (
                                  <tr
                                    key={user.user_id}
                                    onClick={() => handleUserRowClick(user)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white w-48 sticky left-0 z-10 bg-gray-50 dark:bg-gray-700 group-hover:bg-gray-100 dark:group-hover:bg-gray-600">
                                      {user.name || '-'}
                                    </td>
                                    <td
                                      className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 min-w-[18rem] w-[18rem] max-w-[18rem] whitespace-nowrap align-top"
                                      title={user.company && user.company.length > 30 ? user.company : undefined}
                                    >
                                      {truncateText(user.company || '', 30)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {formatCpf(user.cpf)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {user.email || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {formatPhone(user.phone)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {userTipo === 'Indicação' ? (
                                        <span className="inline-flex items-center gap-1 text-indigo-700 dark:text-indigo-300 font-medium">
                                          Indicação
                                          {indicationRef && (
                                            <UiTooltip content={formatIndicationBrokerTooltip(indicationRef)} position="top">
                                              <button
                                                type="button"
                                                className="inline-flex text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-200"
                                                aria-label="Ver corretor que indicou"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <QuestionMarkCircleIcon className="h-4 w-4" />
                                              </button>
                                            </UiTooltip>
                                          )}
                                        </span>
                                      ) : (
                                        'Segurado'
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {renderSuperappUserStatusBadge(
                                        resolveSuperappUserListStatus(user, usersBehaviorStatusMap[user.user_id])
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {getAssistanceActivationCount(user.cpf, assistanceActivationByCpf)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                      {formatDate(user.created_at)}
                                    </td>
                                  </tr>
                                  )
                                })
                              )}
                            </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Paginação */}
                        {totalPagesUser > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexUser + 1} a {Math.min(endIndexUser, usersFiltrados.length)} de {usersFiltrados.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPageUser}
                                  disabled={currentPageUser === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesUser) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesUser <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPageUser <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPageUser >= totalPagesUser - 2) {
                                      pageNumber = totalPagesUser - 4 + i
                                    } else {
                                      pageNumber = currentPageUser - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangeUser(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPageUser === pageNumber
                                            ? 'bg-primary-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPageUser}
                                  disabled={currentPageUser === totalPagesUser}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'promocoes' && (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {/* Filtros */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                        Filtros
                      </Heading>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Título
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o título..."
                            value={filtroTitle}
                            onChange={(e) => setFiltroTitle(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Estabelecimento
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o nome..."
                            value={filtroEstablishmentName}
                            onChange={(e) => setFiltroEstablishmentName(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Data Início
                          </Text>
                          <Input
                            type="date"
                            value={filtroStartDate}
                            onChange={(e) => setFiltroStartDate(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Data Fim
                          </Text>
                          <Input
                            type="date"
                            value={filtroEndDate}
                            onChange={(e) => setFiltroEndDate(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Tipo
                          </Text>
                          <Input
                            type="text"
                            placeholder="Digite o tipo..."
                            value={filtroType}
                            onChange={(e) => setFiltroType(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFiltroTitle('')
                              setFiltroEstablishmentName('')
                              setFiltroStartDate('')
                              setFiltroEndDate('')
                              setFiltroType('')
                            }}
                          >
                            Limpar Filtros
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Tabela */}
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : (
                      <>
                        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => scrollPromotionsBy(-320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a esquerda"
                          >
                            <ChevronLeftIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => scrollPromotionsBy(320)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            aria-label="Rolar para a direita"
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                          <div ref={promotionsScrollRef} className="overflow-x-auto" onWheel={handlePromotionsWheel}>
                            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                <tr>
                                  <SortableTableHeader
                                    sortKey="title"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Título
                                  </SortableTableHeader>
                                  <SortableTableHeader
                                    sortKey="establishment_name"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Estabelecimento
                                  </SortableTableHeader>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                    Status
                                  </th>
                                  <SortableTableHeader
                                    sortKey="start_date"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Data Início
                                  </SortableTableHeader>
                                  <SortableTableHeader
                                    sortKey="end_date"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Data Fim
                                  </SortableTableHeader>
                                  <SortableTableHeader
                                    sortKey="type"
                                    currentSort={sortByPromotion}
                                    sortOrder={sortOrderPromotion}
                                    onSort={toggleSortPromotion}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                                  >
                                    Tipo
                                  </SortableTableHeader>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                    Código de Cópia
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {promotionsPaginadas.length === 0 ? (
                                  <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                      Nenhuma promoção encontrada
                                    </td>
                                  </tr>
                                ) : (
                                  promotionsPaginadas.map((promotion) => (
                                    <tr
                                      key={promotion.id}
                                      onClick={() => handlePromotionRowClick(promotion)}
                                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    >
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {truncateText(promotion.title, 30)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {promotion.establishment_name}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {isPromotionActive(promotion.start_date, promotion.end_date) ? (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                            Ativa
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                            Inativa
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {formatDate(promotion.start_date)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {formatDate(promotion.end_date)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {promotion.type}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {promotion.copy_code || '-'}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Paginação */}
                        {totalPagesPromotion > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">
                                  Mostrando {startIndexPromotion + 1} a {Math.min(endIndexPromotion, promotionsFiltradas.length)} de {promotionsFiltradas.length} registros
                                </Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={handlePreviousPagePromotion}
                                  disabled={currentPagePromotion === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPagesPromotion) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPagesPromotion <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPagePromotion <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPagePromotion >= totalPagesPromotion - 2) {
                                      pageNumber = totalPagesPromotion - 4 + i
                                    } else {
                                      pageNumber = currentPagePromotion - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => handlePageChangePromotion(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPagePromotion === pageNumber
                                            ? 'bg-primary-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={handleNextPagePromotion}
                                  disabled={currentPagePromotion === totalPagesPromotion}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'momentos_chave' && (
                milestonesPermissionsLoading ? (
                  <div className="flex-1 flex items-center justify-center py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : canAccessMomentosChaveTab ? (
                <div className="flex-1 px-6 py-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
                        Momentos-Chave
                      </Heading>
                      <Text size="sm" color="muted">
                        Cadastre datas relevantes (lançamento, envio de e-mails, mudanças de fluxo) para exibi-las nos gráficos do dashboard.
                      </Text>
                    </div>

                    {(canInsertMilestone || (canUpdateMilestone && editingMilestoneId)) && (
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                          <div className="md:col-span-2">
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                              Nome do marco
                            </Text>
                            <Input
                              type="text"
                              placeholder="Ex.: Lançamento, E-mail campanha de ativação..."
                              value={milestoneFormName}
                              onChange={(e) => setMilestoneFormName(e.target.value)}
                              className="w-full"
                              disabled={milestoneSaving || (editingMilestoneId ? !canUpdateMilestone : !canInsertMilestone)}
                            />
                          </div>
                          <div>
                            <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                              Data
                            </Text>
                            <Input
                              type="date"
                              value={milestoneFormDate}
                              onChange={(e) => setMilestoneFormDate(e.target.value)}
                              className="w-full"
                              disabled={milestoneSaving || (editingMilestoneId ? !canUpdateMilestone : !canInsertMilestone)}
                            />
                          </div>
                        </div>
                        {milestoneFormError ? (
                          <Text size="sm" className="text-red-600 dark:text-red-400 mt-3">
                            {milestoneFormError}
                          </Text>
                        ) : null}
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Button
                            size="sm"
                            onClick={handleSaveMilestone}
                            disabled={
                              milestoneSaving ||
                              (editingMilestoneId ? !canUpdateMilestone : !canInsertMilestone)
                            }
                          >
                            {milestoneSaving ? 'Salvando...' : editingMilestoneId ? 'Atualizar marco' : 'Adicionar marco'}
                          </Button>
                          {editingMilestoneId ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={resetMilestoneForm}
                              disabled={milestoneSaving}
                            >
                              Cancelar edição
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {milestonesLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <LoadingSpinner size="lg" />
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                Nome
                              </th>
                              <SortableTableHeader
                                sortKey="event_date"
                                currentSort="event_date"
                                sortOrder={sortOrderMilestone}
                                onSort={toggleSortMilestone}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                              >
                                Data
                              </SortableTableHeader>
                              {(canUpdateMilestone || canDeleteMilestone) && (
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300">
                                  Ações
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {sortedMilestones.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={canUpdateMilestone || canDeleteMilestone ? 3 : 2}
                                  className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                                >
                                  Nenhum marco cadastrado.
                                </td>
                              </tr>
                            ) : (
                              sortedMilestones.map((milestone) => (
                                <tr key={milestone.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                    {milestone.name}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    {formatDateShort(milestone.event_date)}
                                  </td>
                                  {(canUpdateMilestone || canDeleteMilestone) && (
                                    <td className="px-6 py-4 text-right whitespace-nowrap">
                                      <div className="inline-flex items-center gap-2">
                                        {canUpdateMilestone ? (
                                          <PermissionGuard
                                            moduleName="superapp_dashboard"
                                            permissionType="update"
                                            fallback={null}
                                          >
                                            <button
                                              type="button"
                                              onClick={() => handleEditMilestone(milestone)}
                                              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                              aria-label={`Editar ${milestone.name}`}
                                              disabled={milestoneSaving}
                                            >
                                              <PencilIcon className="h-4 w-4" />
                                            </button>
                                          </PermissionGuard>
                                        ) : null}
                                        {canDeleteMilestone ? (
                                          <PermissionGuard
                                            moduleName="superapp_dashboard"
                                            permissionType="delete"
                                            fallback={null}
                                          >
                                            <button
                                              type="button"
                                              onClick={() => handleDeleteMilestone(milestone.id)}
                                              className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 h-8 w-8 dark:bg-gray-800 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
                                              aria-label={`Excluir ${milestone.name}`}
                                              disabled={milestoneSaving}
                                            >
                                              <TrashIcon className="h-4 w-4" />
                                            </button>
                                          </PermissionGuard>
                                        ) : null}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center py-12">
                    <div className="text-center">
                      <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                        Acesso Negado
                      </Text>
                      <Text size="sm" color="muted">
                        Você não tem permissão para gerenciar momentos-chave.
                      </Text>
                    </div>
                  </div>
                )
              )}

              {/* Modal de Detalhes do Usuário */}
              {isUserModalOpen && selectedUser && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseUserModal}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 max-h-[90vh] flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Detalhes do Usuário
                        </Heading>
                        <button onClick={handleCloseUserModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        {userModalLoading ? (
                          <div className="flex items-center justify-center py-16">
                            <LoadingSpinner size="lg" />
                          </div>
                        ) : (
                          <div className="space-y-8">
                            <div>
                              <Heading as="h4" size="xl" className="text-gray-900 dark:text-white mb-4">
                                Dados do cadastro
                              </Heading>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    ID do usuário
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white break-all">
                                    {selectedUser.user_id}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Nome
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {selectedUser.name || '-'}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Email
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {selectedUser.email || '-'}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    CPF
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {formatCpf(selectedUser.cpf)}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Telefone
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {formatPhone(selectedUser.phone)}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Empresa
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {selectedUser.company || '-'}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Apólice
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {selectedUser.policy_number != null ? String(selectedUser.policy_number) : '-'}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Subgrupo
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {selectedUser.subgroup != null ? String(selectedUser.subgroup) : '-'}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Tipo
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {(() => {
                                      const indicationRef = getUserIndicationRef(selectedUser.cpf, appIndicationByCpf)
                                      if (!indicationRef) return 'Segurado'
                                      return indicationRef.broker_code != null
                                        ? `Indicação - ${indicationRef.broker_name} (cód. ${indicationRef.broker_code})`
                                        : `Indicação - ${indicationRef.broker_name}`
                                    })()}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Status
                                  </Text>
                                  <Text size="sm">
                                    {renderSuperappUserStatusBadge(
                                      resolveSuperappUserListStatus(
                                        selectedUser,
                                        usersBehaviorStatusMap[selectedUser.user_id]
                                      )
                                    )}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Risco de churn
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {(() => {
                                      const st = usersBehaviorStatusMap[selectedUser.user_id]
                                      if (st === undefined) return '-'
                                      return st.risk_churn ? 'Sim' : 'Não'
                                    })()}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Data de criação
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {formatDate(selectedUser.created_at)}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Data de atualização
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {formatDate(selectedUser.updated_at)}
                                  </Text>
                                </div>
                                <div>
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Registros de comportamento
                                  </Text>
                                  <Text size="sm" className="text-gray-900 dark:text-white">
                                    {selectedUserBehaviorCount.toLocaleString('pt-BR')}
                                  </Text>
                                </div>
                                {selectedBehavior?.created_at ? (
                                  <div>
                                    <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                      Data do último snapshot
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white">
                                      {formatDate(selectedBehavior.created_at)}
                                    </Text>
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                              <Heading as="h4" size="xl" className="text-gray-900 dark:text-white mb-4">
                                Comportamento
                              </Heading>
                              {selectedBehavior && behaviorDataToSnapshots(selectedBehavior.behavior_data).length > 0 ? (
                                <div className="space-y-6">
                                  {behaviorDataToSnapshots(selectedBehavior.behavior_data).map((snapshot, index) => (
                                    <div
                                      key={index}
                                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                                    >
                                      {behaviorDataToSnapshots(selectedBehavior.behavior_data).length > 1 ? (
                                        <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-4 font-medium">
                                          Snapshot {index + 1}
                                        </Text>
                                      ) : null}
                                      <UserBehaviorSnapshotPanel
                                        snapshot={snapshot}
                                        formatDate={formatDate}
                                        formatCpf={formatCpf}
                                        formatPhone={formatPhone}
                                        getOpenStreetMapEmbedUrl={getOpenStreetMapEmbedUrl}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                  Nenhum dado de comportamento disponível para este usuário.
                                </Text>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de Detalhes da Promoção */}
              {isPromotionModalOpen && selectedPromotion && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClosePromotionModal}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 max-h-[90vh] flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                          Detalhes da Promoção
                        </Heading>
                        <button onClick={handleClosePromotionModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        <div className="space-y-4">
                          {/* Imagem do Estabelecimento */}
                          {selectedPromotion.establishment_image && (
                            <div className="mb-4">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Imagem do Estabelecimento
                              </Text>
                              <div className="flex justify-center">
                                <img
                                  src={selectedPromotion.establishment_image}
                                  alt={selectedPromotion.establishment_name}
                                  className="max-w-full h-auto max-h-96 rounded-lg shadow-lg"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.style.display = 'none'
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                ID
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.id}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Título
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.title}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                ID do Estabelecimento
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.establishment_id}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Nome do Estabelecimento
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.establishment_name}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                URL
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white break-all">
                                <a
                                  href={selectedPromotion.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 underline"
                                >
                                  {selectedPromotion.url}
                                </a>
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Tipo
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.type}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Data de Início
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatDate(selectedPromotion.start_date)}
                              </Text>
                            </div>
                            <div>
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Data de Fim
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {formatDate(selectedPromotion.end_date)}
                              </Text>
                            </div>
                            <div className="col-span-2">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                                Código de Cópia
                              </Text>
                              <Text size="sm" className="text-gray-900 dark:text-white">
                                {selectedPromotion.copy_code || '-'}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
        </main>
    </div>
  )
}

export default SuperappDashboard
